import { useMemo } from "react";
import { Field } from "redux-form";

import { Typography } from "@braintrust/braintrust-ui-components";
import { setSavedFiltersName } from "@js/apps/common/components/save-filters/slice";
import type { FilterLocation } from "@js/apps/filters/types";
import { useSaveTalentFilterMutation } from "@js/apps/freelancer/api";
import { useCreateStringDependsOnSelectedTalentFilters } from "@js/apps/jobs/apps/listing/hooks/use-create-string-depends-on-selected-filters";
import { useGetSkillsByIds } from "@js/apps/skills/hooks";
import { Snackbar } from "@js/components/snackbar";
import { createFormInstance } from "@js/forms/components";
import { TextField } from "@js/forms/fields";
import { required } from "@js/forms/validators";
import { useAppDispatch } from "@js/hooks";

import type { TalentFiltersFetchParams } from "../../filters";
import { filterSkillsIds, generateSuggestedFilterName } from "../helpers";
import { useSavedTalentsFilters } from "../hooks/use-saved-talents-filters";
import {
  closeSaveFiltersModal,
  SaveFiltersModalInstance,
} from "../save-filters-modal";
import { SaveFiltersModalButtons } from "../save-filters-modal/save-filters-modal-buttons";

type FormData = {
  name: string;
};

type SaveTalentsFiltersModalContentProps = {
  filters: Partial<TalentFiltersFetchParams>;
  location: FilterLocation;
};

export const openSaveTalentsFiltersModal = (
  arg: SaveTalentsFiltersModalContentProps,
) => {
  SaveFiltersModalInstance.open({
    children: <SaveTalentsFiltersModalContent {...arg} />,
  });
};

const SaveTalentsFiltersFormInstance = createFormInstance<FormData, unknown>(
  "save-talents-filters-form",
);

const SaveTalentsFiltersModalContent = ({
  filters,
  location,
}: SaveTalentsFiltersModalContentProps) => {
  const dispatch = useAppDispatch();
  const { savedTalentFilters } = useSavedTalentsFilters();
  const [saveTalentFilter] = useSaveTalentFilterMutation();

  const initialName = useMemo(
    () => generateSuggestedFilterName("Talent", savedTalentFilters),
    [savedTalentFilters],
  );

  const ids = useMemo(() => {
    return filterSkillsIds(filters);
  }, [filters]);
  const { data: skillsData } = useGetSkillsByIds({ ids });

  const filtersString = useCreateStringDependsOnSelectedTalentFilters(
    filters,
    skillsData,
  );

  return (
    <SaveTalentsFiltersFormInstance
      onSubmit={async ({ name }: FormData) => {
        const payload = { filters, name };

        await saveTalentFilter(payload).unwrap();

        closeSaveFiltersModal();

        dispatch(
          setSavedFiltersName({
            location,
            name,
          }),
        );

        Snackbar.success(`Filters saved as "${name}".`);
      }}
      initialValues={{
        name: initialName,
      }}
    >
      <Typography
        component="h1"
        variant="title"
        size="medium"
        sx={{ mb: 1, mt: 5.5 }}
      >
        Save this talent search
      </Typography>
      <Typography component="p" variant="paragraph" size="large" sx={{ mb: 1 }}>
        {filtersString}
      </Typography>
      <Field
        id="talent-filters-name"
        name="name"
        label="Name"
        size="small"
        placeholder="Give your search a name"
        component={TextField}
        validate={[required]}
      />

      <SaveFiltersModalButtons />
    </SaveTalentsFiltersFormInstance>
  );
};
